import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import Spinner from "../components/ui/Spinner";
import Textarea from "../components/ui/Textarea";
import { Card, CardBody, CardHeader, CardSubtitle, CardTitle } from "../components/ui/Card";
import ExhibitViewer from "../components/ExhibitViewer";
import { api } from "../services/api";
import { getApiBase } from "../services/apiBase";
import { getWorkspaceId } from "../services/authStorage";
import { useSession } from "../hooks/useSession";
import ThoughtTrace, { type ThoughtTraceStep } from "../components/agent/ThoughtTrace";
import DraftingCanvas from "../components/DraftingCanvas";
import AigisStatus from "../components/agent/AigisStatus";
import { useTeleport } from "../contexts/TeleportContext";
import { parseCitations } from "../utils/citationParser";
import { computeTeleportSelection } from "../utils/teleport";
import SplitPaneViewer from "../components/SplitPaneViewer";
import { getCsrfHeader } from "../services/csrf";
import { HALLUCINATION_RISK_MSG } from "../../shared/constants";

type Exhibit = {
  id: string;
  title?: string | null;
  filename: string;
  integrityHash?: string | null;
  matterId?: string | null;
  type?: "PDF" | "VIDEO" | "AUDIO";
};
type Anchor = {
  id: string;
  exhibitId: string;
  pageNumber: number;
  lineNumber: number;
  text: string;
  bboxJson: [number, number, number, number] | null;
};

interface CaseAssistantProps {
  workspaceId: string;
  token: string;
  onCitationClick?: (citation: { pageNumber: number; bbox?: [number, number, number, number]; exhibitId?: string }) => void;
}

export default function CaseAssistant({ workspaceId, token, onCitationClick }: CaseAssistantProps) {
  const teleportTestEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("teleportTest") === "1";
  }, []);
  const resolvedWorkspaceId = workspaceId
    || getWorkspaceId()
    || (teleportTestEnabled ? "w1" : "");
  const { authed } = useSession();
  const navigate = useNavigate();

  const [exhibits, setExhibits] = useState<Exhibit[]>([]);
  const [activeExhibitId, setActiveExhibitId] = useState<string>("");

  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<"all" | "page">("all");
  const [accessLocked, setAccessLocked] = useState(false);
  const [mode, setMode] = useState<"agent" | "auto">("agent");
  const [requireApproval, setRequireApproval] = useState(false);
  const [pendingApproval, setPendingApproval] = useState<{ id: string; action: string; input: string } | null>(null);
  const [approvalCountdown, setApprovalCountdown] = useState<number | null>(null);
  const [compliancePulse, setCompliancePulse] = useState<"idle" | "intent" | "provenance" | "consensus" | "redacted">("idle");
  const [withheldNotice, setWithheldNotice] = useState<{
    subtitle: string;
    code?: string;
    reasons?: string[];
    rejectedDraft?: string;
    auditEventId?: string | null;
  } | null>(null);

  const [answer, setAnswer] = useState<string>("");
  const [generalConfidence, setGeneralConfidence] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimeoutRef = useRef<number | null>(null);
  const showToast = (message: string) => {
    setToast(message);
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 4000);
  };
  const [claims, setClaims] = useState<any[]>([]);
  const [findings, setFindings] = useState<any[]>([]);
  const [anchorsById, setAnchorsById] = useState<Record<string, Anchor>>({});
  const [evidenceUsedCount, setEvidenceUsedCount] = useState<number | null>(null);
  const [metrics, setMetrics] = useState<{
    totalClaims?: number;
    anchoredCount?: number;
    unanchoredCount?: number;
    returnedUnanchoredClaimRate?: number;
  } | null>(null);
  const [evidenceExhibits, setEvidenceExhibits] = useState<Array<{ exhibitId: string; integrityHash: string }>>([]);
  const [anchorCount, setAnchorCount] = useState<number | null>(null);
  const [traceSteps, setTraceSteps] = useState<ThoughtTraceStep[]>([]);
  const traceRef = useRef<ThoughtTraceStep[]>([]);
  const generalAnswerRef = useRef<string>("");
  const [viewMode, setViewMode] = useState<"trace" | "draft">("trace");
  const [mediaStartTime, setMediaStartTime] = useState<number | null>(null);
  const [pendingMedia, setPendingMedia] = useState<{ exhibitId: string; startTime: number } | null>(null);
  const [downloadingPacket, setDownloadingPacket] = useState(false);

  const { target, setTarget, clearTarget } = useTeleport();
  const [pendingFocus, setPendingFocus] = useState<{
    exhibitId: string;
    anchorId?: string;
    pageNumber?: number;
    bbox?: [number, number, number, number] | null;
    requestedAt?: number;
  } | null>(null);
  const anchorCacheRef = useRef<Record<string, Record<string, Anchor>>>({});
  const anchorFetchRef = useRef<Record<string, Promise<void> | null>>({});

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const activeExhibit = useMemo(() => exhibits.find((e) => e.id === activeExhibitId) || null, [exhibits, activeExhibitId]);
  const activeTeleport = useMemo(() => {
    if (!target || target.exhibitId !== activeExhibitId) return null;
    return {
      page: target.pageNumber,
      bbox: target.bbox,
      nonce: target.nonce,
      requestedAt: target.requestedAt,
      switchCompletedAt: target.switchCompletedAt
    };
  }, [target, activeExhibitId]);

  useEffect(() => {
    if (pendingMedia && pendingMedia.exhibitId === activeExhibitId) {
      setMediaStartTime(pendingMedia.startTime);
      setPendingMedia(null);
      return;
    }
  }, [pendingMedia, activeExhibitId]);

  const resolveExhibitIdByLabel = useCallback((label: string) => {
    const normalized = String(label || "").trim().toLowerCase();
    if (!normalized) return null;
    const exact = exhibits.find((e) => {
      const filename = String(e.filename || "").toLowerCase();
      const title = String(e.title || "").toLowerCase();
      return filename === normalized || title === normalized;
    });
    if (exact) return exact.id;
    const fuzzy = exhibits.find((e) => {
      const filename = String(e.filename || "").toLowerCase();
      const title = String(e.title || "").toLowerCase();
      return filename.includes(normalized) || title.includes(normalized) || normalized.includes(filename);
    });
    if (fuzzy) return fuzzy.id;
    if (normalized.includes("police") || normalized.includes("report")) {
      const pdf = exhibits.find((e) => String(e.mimeType || "").includes("pdf"))
        || exhibits.find((e) => String(e.filename || "").toLowerCase().endsWith(".pdf"));
      return pdf?.id || null;
    }
    if (normalized.includes("deposition") || normalized.includes("video")) {
      const video = exhibits.find((e) => String(e.mimeType || "").includes("video"))
        || exhibits.find((e) => String(e.filename || "").toLowerCase().endsWith(".mp4"));
      return video?.id || null;
    }
    if (normalized.includes("linkedin") || normalized.includes("web")) {
      const image = exhibits.find((e) => String(e.mimeType || "").includes("image"))
        || exhibits.find((e) => String(e.filename || "").toLowerCase().endsWith(".png"));
      return image?.id || null;
    }
    return null;
  }, [exhibits]);

  const logTeleport = useCallback((payload: {
    exhibitId: string;
    pageNumber: number;
    anchorId?: string;
    bbox?: [number, number, number, number] | null;
  }) => {
    if (!resolvedWorkspaceId) return;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "x-workspace-id": resolvedWorkspaceId
    };
    Object.assign(headers, getCsrfHeader());
    const body = {
      action: "TELEPORT_VIEW",
      resourceId: payload.exhibitId,
      details: {
        pageNumber: payload.pageNumber,
        anchorId: payload.anchorId,
        bbox: payload.bbox ?? null,
        source: "citation_click"
      }
    };
    void fetch(`${getApiBase()}/audit/log`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      credentials: "include"
    }).catch(() => null);
  }, [resolvedWorkspaceId]);

  const teleportToPage = useCallback((label: string, page: number) => {
    const exhibitId = resolveExhibitIdByLabel(label);
    if (!exhibitId) {
      showToast(`Exhibit not found: ${label}`);
      return;
    }
    const requestedAt = Date.now();
    const normalizedLabel = String(label || "").toLowerCase();
    const fallbackBBox: [number, number, number, number] | undefined =
      normalizedLabel.includes("police") || normalizedLabel.includes("report")
        ? [100, 100, 120, 30]
        : undefined;
    setMediaStartTime(null);
    if (exhibitId !== activeExhibitId) {
      setActiveExhibitId(exhibitId);
    }
    const selection = computeTeleportSelection({
      exhibitId,
      pageNumber: page,
      requestedAt,
      bbox: fallbackBBox ?? null
    });
    setTarget(selection);
    logTeleport({ exhibitId, pageNumber: page, bbox: selection.bbox ?? null });
    onCitationClick?.({ pageNumber: page, exhibitId });
  }, [resolveExhibitIdByLabel, activeExhibitId, setTarget, onCitationClick, logTeleport]);

  const teleportToTimestamp = useCallback((label: string, seconds: number) => {
    const exhibitId = resolveExhibitIdByLabel(label);
    if (!exhibitId) {
      showToast(`Exhibit not found: ${label}`);
      return;
    }
    clearTarget();
    if (exhibitId !== activeExhibitId) {
      setPendingMedia({ exhibitId, startTime: seconds });
      setActiveExhibitId(exhibitId);
      return;
    }
    setMediaStartTime(seconds);
  }, [resolveExhibitIdByLabel, activeExhibitId, clearTarget]);


  const renderCitationText = (text: string) => {
    const segments = parseCitations(text);
    return segments.map((segment, idx) => {
      if (segment.type === "text") {
        return <span key={`t-${idx}`}>{segment.value}</span>;
      }
      if (segment.type === "doc") {
        return (
          <button
            key={`d-${idx}`}
            type="button"
            onClick={() => teleportToPage(segment.label, segment.page)}
            className="text-indigo-300 hover:text-indigo-200 underline underline-offset-2"
          >
            {segment.raw}
          </button>
        );
      }
      return (
        <button
          key={`m-${idx}`}
          type="button"
          onClick={() => teleportToTimestamp(segment.label, segment.seconds)}
          className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
        >
          {segment.raw}
        </button>
      );
    });
  };
  const activeFocus = useMemo(() => {
    if (!target || target.exhibitId !== activeExhibitId) return null;
    return {
      pageNumber: target.pageNumber,
      bbox: target.bbox,
      nonce: target.nonce,
      requestedAt: target.requestedAt
    };
  }, [target, activeExhibitId]);
  const modelLabel = "AIGIS-CORE v4.0";
  const activeMatterId = activeExhibit?.matterId || "";
  const downloadProofPacket = useCallback(async () => {
    if (!resolvedWorkspaceId || !activeMatterId) {
      showToast("Missing workspace or matter context.");
      return;
    }
    try {
      setDownloadingPacket(true);
      const res = await fetch(
        `${getApiBase()}/workspaces/${resolvedWorkspaceId}/matters/${activeMatterId}/final-export`,
        { credentials: "include" }
      );
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Proof packet download failed.");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `LexiPro_Forensic_Export_${activeMatterId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showToast("Proof packet downloaded.");
    } catch (err: any) {
      showToast(err?.message || "Proof packet download failed.");
    } finally {
      setDownloadingPacket(false);
    }
  }, [resolvedWorkspaceId, activeMatterId, showToast]);
  const approvalToolOptions = [
    { id: "search_evidence", label: "Search" },
    { id: "cross_reference_entities", label: "Cross-Ref" },
    { id: "read_document", label: "Read Doc" },
    { id: "analyze_metadata", label: "Metadata" },
    { id: "verify_integrity", label: "Verify Hash" }
  ];
  const [approvalTools, setApprovalTools] = useState<string[]>(approvalToolOptions.map((t) => t.id));
  const starterPrompts = [
    { label: "Deal Value", text: "What is the total proposed purchase price and is it contingent on anything?" },
    { label: "Red Flags", text: "Identify any clauses related to 'Indemnification' or 'Termination' that carry high risk." },
    { label: "Governing Law", text: "Which state laws govern this agreement and where is the venue for disputes?" },
  ];

  const classifyIntentLocal = (input: string) => {
    const text = String(input || "").toLowerCase();
    const forensicHints = [
      "exhibit",
      "evidence",
      "anchor",
      "bates",
      "pdf",
      "document",
      "timeline",
      "audit",
      "claim",
      "integrity"
    ];
    return forensicHints.some((hint) => text.includes(hint)) ? "forensic" : "general";
  };

  const loadPrompt = (text: string) => {
    setPrompt(text);
  };

  const fileObj = useMemo(() => {
    if (!activeExhibit) return null;
    const matterParam = activeExhibit?.matterId ? `?matterId=${encodeURIComponent(activeExhibit.matterId)}` : "";
    const base = getApiBase();
    const url = resolvedWorkspaceId
      ? `${base}/workspaces/${resolvedWorkspaceId}/exhibits/${activeExhibit.id}/file${matterParam}`
      : `${base}/exhibits/${activeExhibit.id}/file${matterParam}`;
    const headers: Record<string, string> = {};
    if (resolvedWorkspaceId) headers["x-workspace-id"] = resolvedWorkspaceId;
    return Object.keys(headers).length ? { url, httpHeaders: headers } : url;
  }, [activeExhibit, resolvedWorkspaceId]);

  useEffect(() => {
    if (!resolvedWorkspaceId || !authed) return;
    api.get(`/workspaces/${resolvedWorkspaceId}/exhibits`)
      .then((rows: any) => {
        const list: Exhibit[] = Array.isArray(rows) ? rows : [];
        setExhibits(list);
        if (!activeExhibitId && list.length) setActiveExhibitId(list[0].id);
      })
      .catch(() => {});
  }, [resolvedWorkspaceId, authed, activeExhibitId]);

  const parseBBox = useCallback((raw: any): [number, number, number, number] | null => {
    if (Array.isArray(raw) && raw.length === 4) {
      const nums = raw.map((n) => Number(n));
      if (nums.every((n) => Number.isFinite(n))) {
        return nums as [number, number, number, number];
      }
      return null;
    }
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return parseBBox(parsed);
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  const normalizeAnchor = useCallback((raw: any): Anchor | null => {
    if (!raw) return null;
    const id = String(raw?.id || "");
    const exhibitId = String(raw?.exhibitId || raw?.exhibit_id || "");
    if (!id || !exhibitId) return null;
    const pageNumber = Number(raw?.pageNumber || raw?.page_number || 1);
    return {
      id,
      exhibitId,
      pageNumber: Number.isFinite(pageNumber) ? pageNumber : 1,
      lineNumber: Number(raw?.lineNumber || raw?.line_number || 0),
      text: String(raw?.text || ""),
      bboxJson: parseBBox(raw?.bboxJson ?? raw?.bbox),
    };
  }, [parseBBox]);

  const cacheAnchors = useCallback((exhibitId: string, anchors: Anchor[]) => {
    const nextMap: Record<string, Anchor> = {};
    anchors.forEach((anchor) => {
      if (anchor?.id) nextMap[String(anchor.id)] = anchor;
    });
    anchorCacheRef.current = { ...anchorCacheRef.current, [exhibitId]: nextMap };
  }, []);

  const prefetchAnchors = useCallback(async (exhibitId: string) => {
    if (!resolvedWorkspaceId || !exhibitId) return;
    if (anchorCacheRef.current[exhibitId]) return;
    if (anchorFetchRef.current[exhibitId]) return anchorFetchRef.current[exhibitId] as Promise<void>;

    const fetchPromise = api
      .get(`/workspaces/${resolvedWorkspaceId}/exhibits/${exhibitId}/anchors`)
      .then((rows: any[]) => {
        const normalized = Array.isArray(rows)
          ? rows.map((row) => normalizeAnchor(row)).filter(Boolean) as Anchor[]
          : [];
        cacheAnchors(exhibitId, normalized);
      })
      .catch(() => null)
      .finally(() => {
        anchorFetchRef.current[exhibitId] = null;
      });

    anchorFetchRef.current[exhibitId] = fetchPromise;
    return fetchPromise;
  }, [cacheAnchors, normalizeAnchor, resolvedWorkspaceId]);

  const describeExhibit = useCallback((exhibitId: string) => {
    const found = exhibits.find((exhibit) => exhibit.id === exhibitId);
    return found?.filename || found?.title || exhibitId.slice(0, 8);
  }, [exhibits]);

  useEffect(() => {
    if (!activeExhibitId) return;
    void prefetchAnchors(activeExhibitId);
  }, [activeExhibitId, prefetchAnchors]);

  useEffect(() => {
    if (!target?.exhibitId) return;
    if (target.exhibitId !== activeExhibitId) {
      setActiveExhibitId(target.exhibitId);
      showToast(`Switched to Exhibit: ${describeExhibit(target.exhibitId)}`);
      void prefetchAnchors(target.exhibitId);
    }
  }, [target?.exhibitId, activeExhibitId, describeExhibit, prefetchAnchors]);

  useEffect(() => {
    if (!pendingFocus) return;
    const cached = pendingFocus.anchorId
      ? (anchorsById[pendingFocus.anchorId] || anchorCacheRef.current[pendingFocus.exhibitId]?.[pendingFocus.anchorId])
      : null;
    const pageNumber = cached?.pageNumber || pendingFocus.pageNumber || 1;
    const bbox = cached?.bboxJson || pendingFocus.bbox || null;

    const selection = computeTeleportSelection({
      exhibitId: pendingFocus.exhibitId,
      pageNumber,
      bbox: bbox || null,
      anchorId: cached?.id || pendingFocus.anchorId,
      switchCompletedAt: Date.now(),
      requestedAt: pendingFocus.requestedAt
    });
    setTarget(selection);
    logTeleport({
      exhibitId: pendingFocus.exhibitId,
      pageNumber,
      anchorId: cached?.id || pendingFocus.anchorId,
      bbox: selection.bbox ?? null
    });

    if (!bbox) {
      showToast(`BBox unavailable - navigated to page ${pageNumber}.`);
    }
    setPendingFocus(null);
  }, [pendingFocus, anchorsById, setTarget, showToast, logTeleport]);

  useEffect(() => {
    if (mode !== "agent" && requireApproval) {
      setRequireApproval(false);
      setPendingApproval(null);
      setApprovalCountdown(null);
    }
  }, [mode, requireApproval]);

  useEffect(() => {
    if (!pendingApproval) {
      setApprovalCountdown(null);
      return;
    }
    const startedAt = Date.now();
    const tick = () => {
      const remaining = 60 - Math.floor((Date.now() - startedAt) / 1000);
      setApprovalCountdown(Math.max(0, remaining));
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [pendingApproval]);

  useEffect(() => {
    if (approvalCountdown === 0 && pendingApproval) {
      showToast("Tool approval timed out. The agent skipped the action.");
    }
  }, [approvalCountdown, pendingApproval]);

  const parseApiError = (err: any) => {
    const raw = String(err?.message || "AI request failed");
    let errorCode: string | undefined;
    let reasons: string[] | undefined;
    if (raw.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.error === "AI_DISABLED") {
          return { message: "AI is disabled. Set GEMINI_API_KEY in server/.env and restart the backend." };
        }
          if (parsed?.errorCode === "NO_ANCHOR_NO_OUTPUT" || parsed?.errorCode === "WITHHELD") {
            errorCode = parsed.errorCode;
            reasons = Array.isArray(parsed.reasons || parsed.withheldReasons)
              ? (parsed.reasons || parsed.withheldReasons).map(String)
              : undefined;
            const rejectedDraft = parsed.rejectedDraft || "Content withheld.";
            const reasonMessage = parsed.errorCode === "WITHHELD" && Array.isArray(reasons) && reasons.includes("INVALID_SCHEMA")
              ? "WITHHELD: Invalid schema"
              : HALLUCINATION_RISK_MSG;
            return {
              message: reasonMessage,
              errorCode,
              reasons,
              rejectedDraft,
              auditEventId: parsed.auditEventId || null
            };
          }
        if (typeof parsed?.error === "string") {
          try {
            const nested = JSON.parse(parsed.error);
            const nestedMsg = nested?.error?.message || nested?.message;
            const nestedStatus = nested?.error?.status || nested?.status;
            if (nestedStatus === "RESOURCE_EXHAUSTED") {
              return { message: "Gemini quota exceeded. Check billing/quota limits or wait and retry." };
            }
            if (nestedMsg) return { message: String(nestedMsg) };
          } catch {
            // fall through
          }
        }
        if (parsed?.error) return { message: `${parsed.error}${parsed.detail ? `: ${parsed.detail}` : ""}` };
      } catch {
        // fall through
      }
    }
    if (raw.includes("AI_DISABLED")) {
      return { message: "AI is disabled. Set GEMINI_API_KEY in server/.env and restart the backend." };
    }
    if (raw.includes("RESOURCE_EXHAUSTED")) {
      return { message: "Gemini quota exceeded. Check billing/quota limits or wait and retry." };
    }
    if (raw.includes("NO_ANCHOR_NO_OUTPUT")) {
      return { message: HALLUCINATION_RISK_MSG, errorCode: "NO_ANCHOR_NO_OUTPUT" };
    }
    return { message: raw };
  };

  const scoreGeneralConfidence = (text: string) => {
    const lower = String(text || "").toLowerCase();
    let score = 90;
    if (lower.length < 60) score -= 15;
    if (!/(source|citation|evidence)/i.test(lower)) score -= 25;
    if (/(maybe|probably|generally|typically|i think|it seems)/i.test(lower)) score -= 20;
    if (/(cannot|not a lawyer|not legal advice)/i.test(lower)) score += 5;
    return Math.max(0, Math.min(100, score));
  };

  const isValidFinding = (finding: any) => {
    const hasIds = typeof finding?.exhibitId === "string" && typeof finding?.anchorId === "string";
    const hasPage = Number.isFinite(Number(finding?.page_number));
    const bbox = finding?.bbox;
    const hasBbox = Array.isArray(bbox) && bbox.length === 4 && bbox.every((n: any) => Number.isFinite(Number(n)));
    const hasHash = typeof finding?.integrityHash === "string" && finding.integrityHash.length >= 16;
    return hasIds && hasPage && hasBbox && hasHash;
  };

  const pushTrace = (step: ThoughtTraceStep) => {
    traceRef.current = [...traceRef.current, step];
    setTraceSteps(traceRef.current);
  };

  const resetTrace = () => {
    traceRef.current = [];
    setTraceSteps([]);
  };

  const runAgentStream = async () => {
    if (!resolvedWorkspaceId) {
      setError("Workspace ID required for agent mode.");
      return;
    }
    setCompliancePulse("consensus");
    resetTrace();
    setAnswer("");
    setGeneralConfidence(null);
    setClaims([]);
    setFindings([]);
    setAnchorsById({});
    setEvidenceUsedCount(null);
    setMetrics(null);
    setEvidenceExhibits([]);
    setAnchorCount(null);
    clearTarget();

    const approvalParam = requireApproval ? "&requireApproval=1" : "";
    const approvalToolsParam = requireApproval
      ? `&approvalTools=${encodeURIComponent(approvalTools.length ? approvalTools.join(",") : "none")}`
      : "";
    const matterParam = activeMatterId ? `&matterId=${encodeURIComponent(activeMatterId)}` : "";
    const url = `${getApiBase()}/workspaces/${encodeURIComponent(resolvedWorkspaceId)}/ai/agent/stream?goal=${encodeURIComponent(prompt)}${approvalParam}${approvalToolsParam}${matterParam}`;
    const source = new EventSource(url, { withCredentials: true } as EventSourceInit);

    source.addEventListener("step", (event: MessageEvent) => {
      try {
        const step = JSON.parse(event.data) as ThoughtTraceStep;
        const id = step.id || `${step.type}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        pushTrace({ ...step, id });
      } catch {
        // ignore malformed step
      }
    });

    source.addEventListener("approval_required", (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data || "{}");
        if (payload?.approvalId) {
          setPendingApproval({
            id: payload.approvalId,
            action: payload.action || "unknown_action",
            input: payload.input || ""
          });
          setCompliancePulse("intent");
        }
      } catch {
        // ignore malformed approval
      }
    });

    source.addEventListener("done", (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data || "{}");
        setAnswer(payload.answer || "");
        if (payload.status === "REDACTED") {
          setCompliancePulse("redacted");
        } else {
          setCompliancePulse("provenance");
        }
      } catch {
        // ignore
      } finally {
        source.close();
        setBusy(false);
        setPendingApproval(null);
        setApprovalCountdown(null);
      }
    });

    source.addEventListener("error", () => {
      source.close();
      setError("Agent stream failed.");
      setCompliancePulse("redacted");
      setBusy(false);
      setPendingApproval(null);
      setApprovalCountdown(null);
    });
  };

  const respondApproval = async (decision: "approve" | "reject") => {
    if (!pendingApproval || !resolvedWorkspaceId) return;
    try {
      await api.post(`/workspaces/${resolvedWorkspaceId}/ai/agent/approve`, {
        approvalId: pendingApproval.id,
        decision
      });
    } catch {
      setError("Failed to submit approval decision.");
    } finally {
      setPendingApproval(null);
    }
  };

  const runGeneralStream = async () => {
    return;
  };

  const run = async () => {
    setBusy(true);
    setError(null);
    setWithheldNotice(null);
    clearTarget();
    resetTrace();
    setCompliancePulse("idle");
    setAnswer("");
    setClaims([]);
    setFindings([]);
    setAnchorsById({});
    setEvidenceUsedCount(null);
    setMetrics(null);
    setEvidenceExhibits([]);
    setAnchorCount(null);
    clearTarget();
    generalAnswerRef.current = "";

    try {
      if (mode === "agent") {
        return await runAgentStream();
      }

      if (mode === "auto") {
        return await runAgentStream();
      }

      setCompliancePulse("provenance");
      const res = await api.chatWithAI(
        prompt,
        resolvedWorkspaceId,
        token,
        activeMatterId
      );

      setAnswer("");
      setGeneralConfidence(null);
      setClaims([]);
      const _findings = Array.isArray(res?.findings) ? res.findings : [];
      const validFindings = _findings.filter(isValidFinding);
      if (_findings.length && validFindings.length !== _findings.length) {
        setWithheldNotice({ subtitle: "Findings failed grounding validation." });
        setAnswer("");
        setClaims([]);
        setFindings([]);
        return;
      } else {
        setFindings(validFindings);
      }
      const anchoredCount = typeof res?.metrics?.anchoredCount === "number" ? res.metrics.anchoredCount : null;
      const totalClaims = typeof res?.metrics?.totalClaims === "number" ? res.metrics.totalClaims : null;
      setEvidenceUsedCount(anchoredCount ?? totalClaims ?? null);
      setMetrics(res?.metrics || null);
      const rawAnchors = res?.anchorsById || {};
      const normalized: Record<string, Anchor> = {};
      for (const [id, anchor] of Object.entries(rawAnchors)) {
        const anyAnchor: any = anchor as any;
        let bbox = anyAnchor?.bboxJson ?? anyAnchor?.bbox ?? null;
        if (typeof bbox === "string") {
          try {
            bbox = JSON.parse(bbox);
          } catch {
            bbox = null;
          }
        }
        normalized[id] = {
          ...anyAnchor,
          id: anyAnchor?.id || id,
          bboxJson: Array.isArray(bbox) ? bbox : null,
        } as Anchor;
      }
      setAnchorsById(normalized);
      setAnchorCount(Object.keys(normalized).length || 0);
      const exhibitHashMap = new Map<string, string>();
      Object.values(normalized).forEach((anchor) => {
        if (!anchor?.exhibitId) return;
        const integrityHash = String((anchor as any)?.integrityHash || "");
        if (integrityHash) {
          exhibitHashMap.set(String(anchor.exhibitId), integrityHash);
        }
      });
      setEvidenceExhibits(
        Array.from(exhibitHashMap.entries()).map(([exhibitId, integrityHash]) => ({
          exhibitId,
          integrityHash
        }))
      );
    } catch (e: any) {
      const parsed = parseApiError(e);
      if (parsed.errorCode === "NO_ANCHOR_NO_OUTPUT" || parsed.errorCode === "WITHHELD") {
        setWithheldNotice({
          subtitle: "No Anchor - No Output",
          code: parsed.errorCode,
          reasons: parsed.reasons,
          rejectedDraft: (parsed as any).rejectedDraft,
          auditEventId: (parsed as any).auditEventId || null
        });
        setAnswer("");
        setClaims([]);
        setFindings([]);
      } else {
        setError(parsed.message);
      }
    } finally {
      if (mode !== "agent") {
        setBusy(false);
      }
    }
  };

  const clickClaim = async (
    anchorId: string,
    options?: { exhibitId?: string; pageNumber?: number; bbox?: [number, number, number, number] | null },
  ) => {
    const requestedAt = Date.now();
    let anchor = anchorsById[anchorId];
    const hintedExhibitId = options?.exhibitId || anchor?.exhibitId;

    if (!anchor && hintedExhibitId) {
      const cached = anchorCacheRef.current[hintedExhibitId]?.[anchorId];
      if (cached) {
        anchor = cached;
      } else {
        await prefetchAnchors(hintedExhibitId);
        anchor = anchorCacheRef.current[hintedExhibitId]?.[anchorId] || anchorsById[anchorId];
      }
    }
    if (anchor && !anchor.bboxJson && hintedExhibitId) {
      await prefetchAnchors(hintedExhibitId);
      anchor = anchorCacheRef.current[hintedExhibitId]?.[anchorId] || anchor;
    }

    if (!anchor && hintedExhibitId) {
      const fallbackPage = options?.pageNumber || 1;
      const bbox = options?.bbox || null;
      if (hintedExhibitId !== activeExhibitId) {
        setActiveExhibitId(hintedExhibitId);
        setPendingFocus({ exhibitId: hintedExhibitId, anchorId, pageNumber: fallbackPage, bbox, requestedAt });
        if (!bbox) {
          showToast(`BBox unavailable - navigated to page ${fallbackPage}.`);
        }
        return;
      }
      const fallbackSelection = computeTeleportSelection({
        exhibitId: hintedExhibitId,
        pageNumber: fallbackPage,
        bbox,
        anchorId,
        requestedAt
      });
      setTarget(fallbackSelection);
      logTeleport({
        exhibitId: hintedExhibitId,
        pageNumber: fallbackPage,
        anchorId,
        bbox: fallbackSelection.bbox ?? null
      });
      if (!bbox) {
        showToast(`BBox unavailable - navigated to page ${fallbackPage}.`);
      }
      onCitationClick?.({ pageNumber: fallbackPage, bbox: bbox || undefined, exhibitId: hintedExhibitId });
      return;
    }

    if (!anchor) {
      showToast("Anchor metadata unavailable for this citation.");
      return;
    }

    const targetExhibitId = anchor.exhibitId;
    const pageNumber = anchor.pageNumber || options?.pageNumber || 1;
    const bbox = anchor.bboxJson || options?.bbox || null;

    if (targetExhibitId !== activeExhibitId) {
      setActiveExhibitId(targetExhibitId);
      setPendingFocus({ exhibitId: targetExhibitId, anchorId: anchor.id, pageNumber, bbox, requestedAt });
      showToast(`Switched to Exhibit: ${describeExhibit(targetExhibitId)}`);
      return;
    }

    const selection = computeTeleportSelection({
      exhibitId: targetExhibitId,
      pageNumber,
      bbox,
      anchorId: anchor.id,
      requestedAt
    });
    setTarget(selection);
    logTeleport({
      exhibitId: targetExhibitId,
      pageNumber,
      anchorId: anchor.id,
      bbox: selection.bbox ?? null
    });

    if (!bbox) {
      showToast(`BBox unavailable - navigated to page ${pageNumber}.`);
    }
    onCitationClick?.({ pageNumber, bbox: bbox || undefined, exhibitId: targetExhibitId });
  };

  
  return (
    <div className="h-full flex flex-col bg-slate-950 text-slate-100 relative">
      <div className="h-12 border-b border-white/5 bg-black/40 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <div className="text-[10px] uppercase tracking-[0.4em] text-slate-400">Case Assistant</div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-900/60 px-2 py-0.5 text-[10px] font-mono text-teal-300">
            <span className="h-1.5 w-1.5 rounded-full bg-teal-400" />
            {modelLabel}
          </div>
          <div className="text-[10px] text-slate-400 font-mono">
            {exhibits.length} Evidence Items Indexed
          </div>
          <AigisStatus pulse={compliancePulse} />
          <div className="hidden xl:flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
            <span className="rounded-full border border-white/10 bg-slate-900/60 px-2 py-0.5 text-slate-300">Workspace</span>
            <span className="font-mono text-slate-300">{resolvedWorkspaceId || "unscoped"}</span>
          </div>
        </div>
        <div className="hidden lg:flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
          <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-emerald-200">Ethical Wall</span>
          <span className="font-mono text-slate-300">Matter: {activeMatterId || "missing"}</span>
        </div>
      </div>
      {toast ? (
        <div className="fixed top-16 right-6 z-50 rounded-xl border border-amber-500/40 bg-amber-950/90 px-4 py-3 text-xs text-amber-100 shadow-lg">
          {toast}
        </div>
      ) : null}
      {teleportTestEnabled ? (
        <div id="teleport-test-panel" className="fixed top-16 left-6 z-50 rounded-xl border border-indigo-500/40 bg-indigo-950/90 px-4 py-3 text-xs text-indigo-100 shadow-lg">
          <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-200 mb-2">Teleport Test</div>
          <div className="flex flex-wrap gap-2">
            <button
              id="teleport-case-bbox"
              type="button"
              className="rounded-full border border-indigo-400/50 px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-indigo-500/20"
              onClick={() => clickClaim("a1", { exhibitId: "ex1", pageNumber: 1, bbox: [100, 100, 120, 30] })}
            >
              bbox
            </button>
            <button
              id="teleport-case-anchor"
              type="button"
              className="rounded-full border border-indigo-400/50 px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-indigo-500/20"
              onClick={() => clickClaim("a1", { exhibitId: "ex1", pageNumber: 1 })}
            >
              anchor
            </button>
            <button
              id="teleport-case-missing"
              type="button"
              className="rounded-full border border-indigo-400/50 px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-indigo-500/20"
              onClick={() => clickClaim("a-missing", { exhibitId: "ex1", pageNumber: 2 })}
            >
              missing bbox
            </button>
            <button
              id="teleport-case-switch"
              type="button"
              className="rounded-full border border-indigo-400/50 px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-indigo-500/20"
              onClick={() => clickClaim("a2", { exhibitId: "ex2", pageNumber: 1 })}
            >
              switch exhibit
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex-1 min-h-0 p-4">
        <SplitPaneViewer
          left={(
            <div className="flex flex-col min-h-0 h-full relative">
          <div className="flex-1 overflow-y-auto pr-2 space-y-3 relative">
            <div className="flex items-center justify-between rounded-xl border border-white/10 bg-slate-900/50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.35em] text-slate-400">Workspace Brief</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode("trace")}
                  className={`rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.2em] ${
                    viewMode === "trace"
                      ? "border-indigo-400/60 bg-indigo-500/20 text-indigo-100"
                      : "border-white/10 text-slate-300 hover:border-white/20"
                  }`}
                >
                  Trace
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("draft")}
                  id="btn-view-draft"
                  data-testid="btn-view-draft"
                  className={`rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.2em] ${
                    viewMode === "draft"
                      ? "border-emerald-400/60 bg-emerald-500/20 text-emerald-100"
                      : "border-white/10 text-slate-300 hover:border-white/20"
                  }`}
                >
                  Draft
                </button>
              </div>
            </div>
            {viewMode === "trace" ? (
              traceSteps.length ? (
                <div className="rounded-xl border border-white/10 bg-slate-900/50 p-3">
                  <div className="text-[10px] uppercase tracking-[0.35em] text-slate-400 mb-2">Thought Trace</div>
                  <ThoughtTrace steps={traceSteps} />
                </div>
              ) : null
            ) : (
              <DraftingCanvas
                workspaceId={resolvedWorkspaceId}
                exhibitId={activeExhibitId}
                matterId={activeMatterId}
                onDocCitationClick={teleportToPage}
                onMediaCitationClick={teleportToTimestamp}
              />
            )}
            {pendingApproval ? (
              <div className="rounded-xl border border-amber-500/40 bg-amber-950/30 p-3">
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-2">Tool Approval Required</div>
                <div className="text-xs text-amber-100">
                  Action: <span className="font-mono">{pendingApproval.action}</span>
                </div>
                <div className="mt-1 text-xs text-amber-200">
                  Input: <span className="font-mono">{pendingApproval.input || "none"}</span>
                </div>
                {approvalCountdown !== null ? (
                  <div className={`mt-2 text-[10px] font-mono ${approvalCountdown <= 10 ? "text-amber-200" : "text-amber-300"}`}>
                    Approval window: {approvalCountdown}s remaining
                  </div>
                ) : null}
                <div className="mt-3 flex items-center gap-2">
                  <Button variant="primary" onClick={() => respondApproval("approve")}>
                    Approve Tool
                  </Button>
                  <Button variant="secondary" onClick={() => respondApproval("reject")}>
                    Reject
                  </Button>
                </div>
              </div>
            ) : null}

            {error ? (
              <div className="rounded-xl border border-red-500/40 bg-red-950/30 p-4 text-sm text-red-200">
                {error}
              </div>
            ) : null}

            {withheldNotice ? (
              <div className="rounded-xl border border-red-500/40 bg-red-950/30 p-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-red-300 mb-2">
                  WITHHELD: No Anchor — Hallucination Risk Detected
                </div>
                <div className="text-sm text-red-200">
                  {HALLUCINATION_RISK_MSG.split(". ")[1] || "Attach a source anchor to release output."}
                </div>
                <div className="text-[10px] text-red-300 mt-2">Audit event recorded.</div>
                {withheldNotice.auditEventId ? (
                  <button
                    type="button"
                    className="mt-3 rounded-full border border-red-400/50 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-red-100 hover:bg-red-500/20"
                    onClick={() => navigate(`/security?auditEventId=${withheldNotice.auditEventId}`)}
                  >
                    View Audit Event
                  </button>
                ) : null}
              </div>
            ) : null}

            {!withheldNotice && findings.length === 0 && claims.length === 0 && !answer ? (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-[10px] uppercase tracking-[0.5em] text-slate-700">
                  Awaiting command input
                </div>
              </div>
            ) : null}

            {!withheldNotice && answer ? (
              <div className="rounded-xl border border-white/10 bg-slate-900/50 p-4">
                <div className="text-[10px] uppercase tracking-[0.35em] text-slate-400 mb-2">AI Response</div>
                <div className="text-sm text-slate-100 leading-relaxed whitespace-pre-wrap">
                  {renderCitationText(answer)}
                </div>
              </div>
            ) : null}

            {!withheldNotice && (metrics || anchorCount !== null || evidenceExhibits.length) ? (
              <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  <span>Evidence Capsule</span>
                  <span className={`rounded-full border px-2 py-0.5 ${
                    metrics?.unanchoredCount ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                  }`}>
                    {metrics?.unanchoredCount ? "WITHHELD" : "RELEASED"}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-slate-300">
                  <div className="rounded-lg border border-white/10 bg-black/40 p-2">
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Anchored claims</div>
                    <div className="mt-1 font-mono text-slate-100">
                      {metrics?.anchoredCount ?? 0} / {metrics?.totalClaims ?? 0}
                    </div>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-black/40 p-2">
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Anchors used</div>
                    <div className="mt-1 font-mono text-slate-100">{anchorCount ?? 0}</div>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-black/40 p-2">
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Exhibits proven</div>
                    <div className="mt-1 font-mono text-slate-100">{evidenceExhibits.length}</div>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] text-slate-400">
                  <span className="rounded-full border border-white/10 bg-black/30 px-2 py-1">
                    Ungrounded withheld: {metrics?.unanchoredCount ?? 0}
                  </span>
                  {typeof metrics?.returnedUnanchoredClaimRate === "number" ? (
                    <span className="rounded-full border border-white/10 bg-black/30 px-2 py-1">
                      Rejection rate: {Math.round(metrics.returnedUnanchoredClaimRate * 100)}%
                    </span>
                  ) : null}
                </div>
                {evidenceExhibits.length ? (
                  <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-slate-400">
                    {evidenceExhibits.slice(0, 3).map((item) => (
                      <span key={item.exhibitId} className="mono rounded-full border border-white/10 bg-black/30 px-2 py-1">
                        {item.exhibitId}: {item.integrityHash.slice(0, 10)}...
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            {!withheldNotice && findings.length ? (
              findings.map((f, idx) => {
                const hash = String(f?.integrityHash || activeExhibit?.integrityHash || "");
                const hashFrag = hash ? hash.slice(0, 12).toLowerCase() : "hash unavailable";
                const confidence = typeof f?.confidence === "number" ? `${Math.round(f.confidence * 100)}%` : "grounded";
                return (
                  <div key={idx} className="rounded-lg border border-white/10 bg-slate-900/50 p-3">
                    <div className="flex items-center justify-between text-xs text-slate-300 mb-2">
                      <span className="font-semibold">Finding {idx + 1}</span>
                      <span className="font-mono text-teal-300">{confidence}</span>
                    </div>
                    <div className="text-sm text-slate-100 whitespace-pre-wrap">
                      {f?.quote || f?.summary || f?.text || "(No quote provided)"}
                    </div>
                    <div className="mt-3 flex items-center gap-3 text-[10px] text-slate-400 font-mono">
                      <span className="mono">SHA-256: {hashFrag}</span>
                      {Number.isFinite(Number(f?.page_number)) ? <span>p.{f.page_number}</span> : null}
                    </div>
                    {f?.anchorId ? (
                      <button
                        className="mt-2 text-[10px] uppercase tracking-widest text-indigo-300 hover:text-indigo-200"
                        onClick={() =>
                          clickClaim(String(f.anchorId), {
                            exhibitId: f?.exhibitId,
                            pageNumber: Number.isFinite(Number(f?.page_number)) ? Number(f.page_number) : undefined,
                            bbox: Array.isArray(f?.bbox) ? f.bbox : null
                          })
                        }
                        type="button"
                      >
                        Jump to Source
                      </button>
                    ) : null}
                  </div>
                );
              })
            ) : null}

            {!withheldNotice && findings.length === 0 ? (
              <div className="rounded-lg border border-white/10 bg-slate-900/50 p-3">
                <div className="text-xs text-slate-400 uppercase tracking-[0.3em] mb-2">No Findings</div>
                <div className="text-sm text-slate-100">
                  No grounded findings returned. Attach anchors to generate admissible output.
                </div>
              </div>
            ) : null}
          </div>

          {accessLocked && !authed ? (
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-20">
              <div className="rounded-xl border border-amber-500/40 bg-amber-950/30 p-6 text-center">
                <div className="text-xs uppercase tracking-[0.4em] text-amber-300 mb-2">Access Locked</div>
                <div className="text-sm text-amber-100">Sign in to run forensic analysis.</div>
              </div>
            </div>
          ) : null}

          <div className="mt-3 border-t border-white/10 bg-slate-950/95 p-3 sticky bottom-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Mode</span>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value as "agent" | "auto")}
                  className="h-7 rounded-lg bg-slate-900/60 border border-white/10 px-2 text-[10px] text-white"
                >
                  <option value="agent">Investigation</option>
                  <option value="auto">Auto Route</option>
                </select>
              </div>
              <label className="ml-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <input
                  type="checkbox"
                  checked={requireApproval}
                  onChange={(e) => setRequireApproval(e.target.checked)}
                  disabled={mode !== "agent" || busy}
                />
                Approval Gate
              </label>
              {requireApproval && mode === "agent" ? (
                <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  <span>Tools</span>
                  {approvalToolOptions.map((tool) => {
                    const active = approvalTools.includes(tool.id);
                    return (
                      <label key={tool.id} className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setApprovalTools((prev) => {
                              if (checked) {
                                return Array.from(new Set([...prev, tool.id]));
                              }
                              return prev.filter((id) => id !== tool.id);
                            });
                          }}
                          disabled={busy}
                        />
                        <span className={active ? "text-teal-200" : "text-slate-500"}>{tool.label}</span>
                      </label>
                    );
                  })}
                </div>
              ) : null}
              <button
                className={`px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.2em] border ${scope === "all" ? "border-teal-500/50 text-teal-300 bg-teal-500/10" : "border-white/10 text-slate-400"}`}
                onClick={() => setScope("all")}
                type="button"
                aria-pressed={scope === "all"}
              >
                Search All
              </button>
              <button
                className={`px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.2em] border ${scope === "page" ? "border-teal-500/50 text-teal-300 bg-teal-500/10" : "border-white/10 text-slate-400"}`}
                onClick={() => setScope("page")}
                type="button"
                aria-pressed={scope === "page"}
              >
                Current Page Only
              </button>
              {evidenceUsedCount !== null ? (
                <span className="text-[10px] text-slate-400 font-mono ml-auto">
                  Anchored Claims: {evidenceUsedCount}
                </span>
              ) : null}
            </div>

            <div className="relative">
              <div className="flex gap-2 mb-2 overflow-x-auto pb-1 no-scrollbar">
                {starterPrompts.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => loadPrompt(p.text)}
                    className="px-2 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-[11px] font-medium text-indigo-200 hover:bg-indigo-500/20 hover:border-indigo-400/50 transition-all whitespace-nowrap"
                    type="button"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <Textarea
                rows={2}
                placeholder="Type command to analyze..."
                value={prompt}
                onFocus={() => {
                  if (!authed) setAccessLocked(true);
                }}
                onChange={(e) => {
                  if (!authed) setAccessLocked(true);
                  setPrompt(e.target.value);
                }}
                className="bg-black/60 border border-white/10 text-slate-100 font-mono placeholder:text-slate-500"
                aria-label="Command input"
              />
              {!prompt ? (
                <span className="absolute right-3 bottom-3 text-cyan-300 font-mono animate-cursor-blink">_</span>
              ) : null}
            </div>

            <div className="mt-3 flex items-center gap-2">
              <Button variant="primary" onClick={run} disabled={busy || !prompt.trim() || !authed || activeExhibit?.verificationStatus === "REVOKED"}>
                {busy ? <Spinner size={16} /> : <i className="fa-solid fa-bolt" />}
                Run Analysis
              </Button>
              <Button
                variant="secondary"
                onClick={downloadProofPacket}
                disabled={downloadingPacket || !authed}
              >
                {downloadingPacket ? <Spinner size={16} /> : <i className="fa-solid fa-download" />}
                Download Admissible Proof Packet
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setPrompt("");
                  setAnswer("");
                  setClaims([]);
                  setAnchorsById({});
                  setEvidenceUsedCount(null);
    clearTarget();
                }}
              >
                Clear
              </Button>
            </div>
          </div>
            </div>
          )}
          right={(
            <div className="min-h-0 h-full">
          <Card className="h-full">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle>Evidence Viewer</CardTitle>
                  <CardSubtitle>Anchored citations jump to the exact source line.</CardSubtitle>
                </div>
                <select
                  value={activeExhibitId}
                  onChange={(e) => setActiveExhibitId(e.target.value)}
                  id="case-assistant-exhibit"
                  name="caseAssistantExhibit"
                  className="h-9 rounded-lg bg-slate-900/60 border border-white/10 px-3 text-xs text-white"
                >
                  {exhibits.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.title || e.filename}
                    </option>
                  ))}
                </select>
              </div>
            </CardHeader>
            <CardBody className="p-0">
              {activeExhibit && fileObj ? (
                <div className="min-h-[60vh]">
                  <ExhibitViewer
                    file={fileObj as any}
                    focusAnchor={activeFocus}
                    jumpTo={activeTeleport}
                    exhibitId={activeExhibitId}
                    workspaceId={resolvedWorkspaceId}
                    matterId={activeExhibit?.matterId || null}
                    token={token}
                    authed={authed}
                    exhibitName={activeExhibit?.filename}
                    exhibitHash={activeExhibit?.integrityHash}
                    exhibitType={activeExhibit?.type || "PDF"}
                    mediaStartTime={mediaStartTime}
                    onVisiblePageChange={() => {}}
                  />
                </div>
              ) : (
                <div className="p-6 text-slate-300">Select an exhibit to preview.</div>
              )}
            </CardBody>
          </Card>
            </div>
          )}
        />
      </div>
    </div>
  );
}
